import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Divider,
  Autocomplete,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { getAllUsers, transferUserTasks, deactivateUser, type User } from '../../../services/userService.ts';

interface UserTransferProps {
  open: boolean;
  fromUser: User;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UserTransfer({ open, fromUser, onClose, onSuccess }: UserTransferProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [toUser, setToUser] = useState<User | null>(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setToUser(null);
    setComment('');
    setError('');
    setLoadingUsers(true);
    getAllUsers()
      .then((all) => setUsers(all.filter((u) => u.id !== fromUser.id && u.isActive !== false)))
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoadingUsers(false));
  }, [open, fromUser.id]);

  const handleConfirm = async () => {
    if (!toUser) {
      setError('Please select a user to transfer tasks to.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await transferUserTasks(fromUser.id, toUser.id, comment);
      await deactivateUser(fromUser.id);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Operation failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1.15rem', pb: 1 }}>
        Transfer Tasks &amp; Deactivate User
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ mb: 2.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <Typography variant="caption" color="text.secondary">Transfer tasks, keep user record inactive</Typography>
          <Typography fontWeight={700} sx={{ color: '#0f172a' }}>
            {fromUser.userName}
            {fromUser.email ? <Typography component="span" variant="body2" color="text.secondary"> ({fromUser.email})</Typography> : null}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <SwapHorizIcon sx={{ color: '#94a3b8', flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">
            All approval tasks assigned to this user will be reassigned to the selected user, and the source user will remain as an inactive record.
          </Typography>
        </Box>

        <Autocomplete
          options={users}
          value={toUser}
          onChange={(_e, newValue) => { setToUser(newValue); setError(''); }}
          loading={loadingUsers}
          disabled={loading}
          getOptionLabel={(u) => u.email ? `${u.userName} (${u.email})` : u.userName}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return options.filter(
              (u) =>
                u.userName.toLowerCase().includes(q) ||
                (u.email ?? '').toLowerCase().includes(q)
            );
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Transfer To User"
              required
              placeholder="Type to search by name or email"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          sx={{ mb: 2.5 }}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Comment (Optional)"
          placeholder="Reason for task transfer"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={loading}
        />

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none', flex: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !toUser}
          variant="contained"
          sx={{ borderRadius: '8px', textTransform: 'none', flex: 1, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Processing...' : 'Confirm Transfer & Deactivate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
